import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const BG = "#F8F4EE";
const CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const SUCCESS = "#5C8A5C";
const ALERT_RED = "#B85C3A";

function extractAmountFromDetail(detail) {
  if (!detail || typeof detail !== "string") return null;
  const m = detail.match(/[\d\s\u00A0]+(?:,\d{3})*(?:\.\d+)?\s*MAD/i);
  return m ? m[0].replace(/\s+/g, " ").trim() : null;
}

function maskMADInText(text) {
  if (!text || typeof text !== "string") return text;
  return text.replace(/[\d\s\u00A0]+(?:,\d{3})*(?:\.\d+)?\s*MAD/gi, "—");
}

export default function AlertsPage({ isOPCMandate = false, cabinetId = null }) {
  const [projects, setProjects] = useState([]);
  const [financeEntries, setFinanceEntries] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const [niveauFiltre, setNiveauFiltre] = useState("tous");
  const [projetFiltre, setProjetFiltre] = useState("tous");

  const cabinetName =
    typeof window !== "undefined"
      ? localStorage.getItem("yoma_company_name") || "MON CABINET"
      : "MON CABINET";

  useEffect(() => {
    if (cabinetId == null) return;
    loadAll();
  }, [cabinetId]);

  async function loadAll() {
    // 1. Charger les projets (projects a cabinet_id)
    const projectsRes = await supabase.from("projects").select("*").eq("cabinet_id", cabinetId);
    const projectsData = projectsRes.data || [];
    setProjects(projectsData);
    const ids = projectsData.map((p) => p.id);

    // 2. milestones a cabinet_id ; finance et payment_schedule n'ont PAS cabinet_id
    const [milestonesRes, financeRes, scheduleRes] = await Promise.all([
      supabase.from("milestones").select("*").eq("cabinet_id", cabinetId),
      ids.length > 0
        ? supabase.from("finance").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] }),
      ids.length > 0
        ? supabase.from("payment_schedule").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] }),
    ]);

    const financeData = financeRes.data || [];
    const milestonesData = milestonesRes.data || [];
    const scheduleData = scheduleRes.data || [];

    setFinanceEntries(financeData);
    setMilestones(milestonesData);
    setSchedule(scheduleData);

    const computedAlerts = buildAlerts(
      projectsData,
      financeData,
      milestonesData,
      scheduleData
    );
    setAlerts(computedAlerts);
    setLastLoadedAt(new Date());
  }

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }

  function getExpectedAmount(projectBudget, percentage) {
    const budget = Number(projectBudget || 0);
    const pct = Number(percentage || 0);
    return (budget * pct) / 100;
  }

  function buildAlerts(projectsData, financeData, milestonesData, scheduleData) {
    const result = [];

    projectsData.forEach((project) => {
      const projectFinance = financeData.filter(
        (entry) => String(entry.project_id) === String(project.id)
      );

      const projectMilestones = milestonesData.filter(
        (m) => String(m.project_id) === String(project.id)
      );

      const projectSchedule = scheduleData.filter(
        (row) => String(row.project_id) === String(project.id)
      );

      const encaisse = projectFinance
        .filter((entry) => entry.type === "income")
        .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);

      const decaisse = projectFinance
        .filter((entry) => entry.type === "expense")
        .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);

      const solde = encaisse - decaisse;

      if (solde < 0) {
        result.push({
          id: `${project.id}-solde-negatif`,
          niveau: "critique",
          projet: project.name,
          type: "Solde négatif",
          detail: `Le projet a un solde négatif de ${formatMoney(solde)}.`,
        });
      }

      projectSchedule.forEach((row) => {
        const attendu = getExpectedAmount(project.budget, row.percentage);

        const encaisseInstallment = projectFinance
          .filter(
            (entry) =>
              entry.type === "income" &&
              Number(entry.installment_number) === Number(row.installment_number)
          )
          .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);

        if (encaisseInstallment === 0) {
          result.push({
            id: `${project.id}-impaye-${row.installment_number}`,
            niveau: "critique",
            projet: project.name,
            type: "Échéance impayée",
            detail: `L'échéance V${row.installment_number} (${row.label}) est impayée. Attendu : ${formatMoney(attendu)}.`,
          });
        } else if (encaisseInstallment < attendu) {
          result.push({
            id: `${project.id}-partiel-${row.installment_number}`,
            niveau: "attention",
            projet: project.name,
            type: "Échéance partielle",
            detail: `L'échéance V${row.installment_number} est partiellement encaissée. Reçu : ${formatMoney(encaisseInstallment)} / Attendu : ${formatMoney(attendu)}.`,
          });
        }

        const relatedMilestone = projectMilestones.find(
          (m) => Number(m.number) === Number(row.milestone_number)
        );

        if (
          relatedMilestone &&
          relatedMilestone.status === "done" &&
          encaisseInstallment === 0
        ) {
          result.push({
            id: `${project.id}-jalon-sans-paiement-${row.installment_number}`,
            niveau: "attention",
            projet: project.name,
            type: "Jalon validé sans paiement",
            detail: `Le jalon ${row.milestone_number} est validé mais aucun paiement n'est enregistré sur l'échéance V${row.installment_number}.`,
          });
        }
      });

      const totalMilestones = projectMilestones.length;
      const doneMilestones = projectMilestones.filter((m) => m.status === "done").length;
      const progress = totalMilestones > 0 ? Math.round((doneMilestones / totalMilestones) * 100) : 0;

      const impayesCount = result.filter(
        (a) => a.projet === project.name && a.type === "Échéance impayée"
      ).length;

      if (progress < 20 && impayesCount >= 2) {
        result.push({
          id: `${project.id}-projet-bloque`,
          niveau: "critique",
          projet: project.name,
          type: "Projet bloqué",
          detail: `Projet à risque : avancement faible (${progress}%) avec ${impayesCount} échéances impayées.`,
        });
      }
    });

    return result;
  }

  const projetsDisponibles = useMemo(() => {
    return [...new Set(alerts.map((a) => a.projet))].sort();
  }, [alerts]);

  const alertsFiltrees = useMemo(() => {
    return alerts.filter((alert) => {
      const okNiveau = niveauFiltre === "tous" || alert.niveau === niveauFiltre;
      const okProjet = projetFiltre === "tous" || alert.projet === projetFiltre;
      return okNiveau && okProjet;
    });
  }, [alerts, niveauFiltre, projetFiltre]);

  const totalCritiques = alerts.filter((a) => a.niveau === "critique").length;
  const totalAttention = alerts.filter((a) => a.niveau === "attention").length;

  const selectStyle = {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #E7E1D8",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    fontSize: 13,
    color: TAUPE,
    background: CARD,
    minWidth: 180,
    cursor: "pointer",
    outline: "none",
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        background: BG,
        padding: "28px 24px 40px",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        color: TAUPE,
        boxSizing: "border-box",
      }}
    ><style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        @media (max-width: 720px) {
          .alerts-kpi-grid { grid-template-columns: 1fr !important; }
        }
      `}</style><header style={{ marginBottom: 22, maxWidth: 1100, marginLeft: "auto", marginRight: "auto" }}><div style={{ fontSize: 12, color: "#8A7B6D", marginBottom: 6, letterSpacing: "0.04em" }}>
          {cabinetName}
        </div><h1
          style={{
            margin: 0,
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 32,
            fontWeight: 600,
            color: TAUPE,
            lineHeight: 1.2,
          }}
        >
          Alertes
        </h1><p style={{ margin: "8px 0 0", fontSize: 15, color: "#6F6256", fontFamily: "'DM Sans', sans-serif" }}>
          Moteur d&apos;alertes intelligentes YOMA
        </p></header><div
        className="alerts-kpi-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 14,
          marginBottom: 20,
          maxWidth: 1100,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      ><div
          style={{
            background: "#FDEEE8",
            borderRadius: 12,
            padding: "16px 18px",
            border: "1px solid rgba(184, 92, 58, 0.2)",
          }}
        ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9A6B5A", marginBottom: 6 }}>
            Critiques
          </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: ALERT_RED }}>
            {totalCritiques}
          </div></div><div
          style={{
            background: "#FDF6EA",
            borderRadius: 12,
            padding: "16px 18px",
            border: "1px solid rgba(192, 154, 91, 0.35)",
          }}
        ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8A7360", marginBottom: 6 }}>
            Attention
          </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: GOLD }}>
            {totalAttention}
          </div></div><div
          style={{
            background: "#F5EFE6",
            borderRadius: 12,
            padding: "16px 18px",
            border: "1px solid #E7E1D8",
          }}
        ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7A6B5E", marginBottom: 6 }}>
            Total alertes
          </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: TAUPE }}>
            {alerts.length}
          </div></div></div><div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 20,
          maxWidth: 1100,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      ><select style={selectStyle} value={niveauFiltre} onChange={(e) => setNiveauFiltre(e.target.value)}><option value="tous">Tous les niveaux</option><option value="critique">Critique</option><option value="attention">Attention</option></select><select style={selectStyle} value={projetFiltre} onChange={(e) => setProjetFiltre(e.target.value)}><option value="tous">Tous les projets</option>
          {projetsDisponibles.map((projet) => (
            <option key={projet} value={projet}>
              {projet}
            </option>
          ))}
        </select></div><div style={{ maxWidth: 1100, marginLeft: "auto", marginRight: "auto" }}>
        {alertsFiltrees.length === 0 ? (
          <div
            style={{
              background: CARD,
              borderRadius: 14,
              border: "1px solid #E7E1D8",
              padding: "48px 24px",
              textAlign: "center",
            }}
          ><div
              style={{
                width: 48,
                height: 48,
                margin: "0 auto 16px",
                borderRadius: "50%",
                background: "rgba(92, 138, 92, 0.12)",
                border: `2px solid ${SUCCESS}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: SUCCESS,
                fontSize: 22,
                fontWeight: 700,
              }}
            >
              ✓
            </div><p style={{ margin: 0, fontSize: 16, fontFamily: "'DM Sans', sans-serif", color: TAUPE, fontWeight: 500 }}>
              Aucune alerte active — tout est à jour ✓
            </p></div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {alertsFiltrees.map((alert) => {
              const isCritique = alert.niveau === "critique";
              const borderColor = isCritique ? ALERT_RED : GOLD;
              const badgeBg = isCritique ? "#FDEEE8" : "#FDF6EA";
              const badgeColor = isCritique ? ALERT_RED : "#B8893A";
              const badgeLabel = isCritique ? "Critique" : "Attention";
              const montant = isOPCMandate ? null : extractAmountFromDetail(alert.detail);
              const detailAffiche = isOPCMandate ? maskMADInText(alert.detail) : alert.detail;

              return (
                <article
                  key={alert.id}
                  style={{
                    background: CARD,
                    borderRadius: 12,
                    border: "1px solid #E7E1D8",
                    borderLeft: `4px solid ${borderColor}`,
                    padding: "16px 18px",
                    position: "relative",
                    boxShadow: "0 1px 3px rgba(61, 46, 32, 0.06)",
                  }}
                ><div style={{ position: "absolute", top: 14, right: 16 }}><span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: badgeBg,
                        color: badgeColor,
                        border: `1px solid ${isCritique ? "rgba(184,92,58,0.25)" : "rgba(192,154,91,0.35)"}`,
                      }}
                    >
                      {badgeLabel}
                    </span></div><h2
                    style={{
                      margin: "0 100px 6px 0",
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 16,
                      fontWeight: 700,
                      color: TAUPE,
                      lineHeight: 1.35,
                    }}
                  >
                    {alert.type}
                  </h2><p style={{ margin: "0 0 10px", fontSize: 13, color: "#8B7361" }}>{alert.projet}</p><p style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.55, color: TAUPE }}>{detailAffiche}</p>
                  {montant ? (
                    <div style={{ fontSize: 14, fontWeight: 600, color: GOLD, marginBottom: 8 }}>{montant}</div>
                  ) : null}
                  <div style={{ fontSize: 11, color: "#9A9088" }}>
                    {lastLoadedAt
                      ? lastLoadedAt.toLocaleString("fr-FR", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </div></article>
              );
            })}
          </div>
        )}
      </div></main>
  );
}
